import { eventChannel, EventChannel } from 'redux-saga';
import { ForkEffect } from 'redux-saga/effects';
import { delay, getContext, call, put, race, select, take, takeLatest, takeLeading } from 'typed-redux-saga';
import { followWithRetry } from '../utils/followWithRetry';
import type { SubscriptionFollowOutcome } from '../types/SubscriptionFollow.types';
import { RobotCleaningControlActions } from './robotCleaningControlSlice';
import type {
  SendLocateCommandRequestAction,
  SubscribeToPausedStateAction,
  SendCleaningCommandRequestAction,
} from './robotCleaningControlActions.types';
import type { DeviceStatusUpdate } from './types/RobotCleaningControl.types';
import {
  CLEANING_COMMAND_TYPE_TO_ACTION_KEY,
  COMMAND_CONFIRMATION_TIMEOUT_MS,
  LocateStatus,
  REQUEST_ONLY_COMMAND_TYPES,
} from './utils/constants';
import { resolveIsPaused, resolveLocateSettings } from './utils/deviceState';
import { waitUntilCommandTakesEffect, waitUntilLocateSettingTakesEffect } from './utils/commandWaiters';
import { getPraDeviceId } from './utils/praDeviceId';
import {
  getRobotKey,
  dropSettledLocateContradictions,
  pinLocateSetting,
  unpinLocateSetting,
} from './utils/locateSettlePins';
import { selectRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/robotCleaningProgressSelectors';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import {
  BRangeCleaningCommandType,
  Cv50Commands,
  type CommandStatus,
  type GeneralCommandResponse,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

// A reset (the controls unmounting or switching robot) must drop an in-flight command's result:
// takeLeading's worker keeps running past the reset, and its late success/error would otherwise
// write into the state of whichever robot is shown next.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* sendCleaningCommandSaga(action: SendCleaningCommandRequestAction): Generator<unknown, void, any> {
  const { robotCleaningControlService, t } = yield* getContext<IDependencies>('dependencies');
  const actionKey = CLEANING_COMMAND_TYPE_TO_ACTION_KEY[action.payload.commandType] ?? 'command';

  try {
    const { model, robotId, ...command } = action.payload;
    const baseline = yield* select(selectRobotCleaningProgressEntry, robotId);
    const { response, reset } = yield* race({
      response: call(
        model === 'CV50'
          ? robotCleaningControlService.sendGeneralCleaningCommand
          : robotCleaningControlService.sendBRangeCleaningCommand,
        command
      ),
      reset: take(RobotCleaningControlActions.sendCleaningCommandReset),
    });

    if (reset) {
      return;
    }

    // Only the request is confirmed: the robot's state can't reliably show these took effect.
    if (REQUEST_ONLY_COMMAND_TYPES.includes(command.commandType)) {
      // Skipping the last route cancels the run, so the controls stay locked until another task starts.
      if (
        command.commandType === BRangeCleaningCommandType.MissionDriveBackAndSkip &&
        baseline?.deviceTaskId &&
        (baseline.step ?? 0) >= (baseline.stepCount ?? 0)
      ) {
        yield* put(RobotCleaningControlActions.lastRouteSkipped(baseline.deviceTaskId));
      }

      yield* put(RobotCleaningControlActions.sendCleaningCommandSuccess(response as GeneralCommandResponse));

      toast.success({
        message: '',
        description: t(`robotCleaningControl.${actionKey}.requested`) as string,
      });

      return;
    }

    // The mutation only queues the command; the outcome is toasted once the robot shows it.
    toast.success({
      message: '',
      description: t(`robotCleaningControl.${actionKey}.requested`) as string,
    });

    const {
      isConfirmed,
      timedOut,
      reset: resetWhileWaiting,
    } = yield* race({
      isConfirmed: call(waitUntilCommandTakesEffect, { commandType: command.commandType, model, robotId }, baseline),
      timedOut: delay(COMMAND_CONFIRMATION_TIMEOUT_MS),
      reset: take(RobotCleaningControlActions.sendCleaningCommandReset),
    });

    if (resetWhileWaiting) {
      return;
    }

    if (timedOut || !isConfirmed) {
      throw new Error(`The robot did not confirm the ${actionKey} command in time`);
    }

    yield* put(RobotCleaningControlActions.sendCleaningCommandSuccess(response as GeneralCommandResponse));

    toast.success({
      message: '',
      description: t(`robotCleaningControl.${actionKey}.success`) as string,
    });
  } catch (error) {
    console.error(error);
    // The failure is shown inline (error badge), not as a toast.
    yield* put(RobotCleaningControlActions.sendCleaningCommandError({ error }));
  }
}

function* resolveAndSendLocateCommand(
  robotCleaningControlService: IDependencies['robotCleaningControlService'],
  materialNumber: string,
  serialNumber: string,
  commandType: SendLocateCommandRequestAction['payload']['commandType'],
  status: LocateStatus
): Generator<unknown, CommandStatus, any> {
  // The command needs the PRA device id, not the machine id (the machine id is rejected as "does
  // not have permission on this resource").
  const praDeviceId = yield* call(getPraDeviceId, robotCleaningControlService, materialNumber, serialNumber);

  if (!praDeviceId) {
    throw new Error('Device not found');
  }

  return yield* call(robotCleaningControlService.sendDeviceCommand, {
    deviceId: praDeviceId,
    command: { type: commandType, data: { status } },
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* sendLocateCommandSaga(action: SendLocateCommandRequestAction): Generator<unknown, void, any> {
  const { robotCleaningControlService, t } = yield* getContext<IDependencies>('dependencies');
  const { materialNumber, serialNumber, commandType, status } = action.payload;
  const actionKey = commandType === Cv50Commands.Beep ? 'beep' : 'blink';

  // A new press decides the state again, so an earlier pin must not hold it back.
  const robotKey = getRobotKey(materialNumber, serialNumber);

  unpinLocateSetting(robotKey, commandType);

  try {
    const { response, reset } = yield* race({
      response: call(
        resolveAndSendLocateCommand,
        robotCleaningControlService,
        materialNumber,
        serialNumber,
        commandType,
        status
      ),
      reset: take(RobotCleaningControlActions.sendCleaningCommandReset),
    });

    if (reset) {
      return;
    }

    // The toggle stays loading until the device reports the new state.
    const {
      isConfirmed,
      timedOut,
      reset: resetWhileWaiting,
    } = yield* race({
      isConfirmed: call(waitUntilLocateSettingTakesEffect, commandType, status),
      timedOut: delay(COMMAND_CONFIRMATION_TIMEOUT_MS),
      reset: take(RobotCleaningControlActions.sendCleaningCommandReset),
    });

    if (resetWhileWaiting) {
      return;
    }

    if (timedOut || !isConfirmed) {
      throw new Error(`The robot did not confirm the ${actionKey} command in time`);
    }

    pinLocateSetting(robotKey, commandType, status === LocateStatus.On);
    toast.success({
      message: '',
      description: t(
        `robotCleaningControl.${actionKey}.${status === LocateStatus.On ? 'started' : 'stopped'}`
      ) as string,
    });
    yield* put(RobotCleaningControlActions.sendLocateCommandSuccess(response as unknown as CommandStatus));
  } catch (error) {
    console.error(error);
    yield* put(RobotCleaningControlActions.sendLocateCommandError({ error }));

    toast.error({
      message: t(`robotCleaningControl.${actionKey}.error`) as string,
      description: (error as Error).message,
    });
  }
}

type PausedStateChannelEvent = { kind: 'status'; update: DeviceStatusUpdate } | { kind: 'error'; error: unknown };

const createPausedStateChannel = (
  subscribeToDevicePausedState: IDependencies['robotCleaningControlService']['subscribeToDevicePausedState'],
  payload: SubscribeToPausedStateAction['payload']
): EventChannel<PausedStateChannelEvent> =>
  eventChannel<PausedStateChannelEvent>(emit => {
    const { model, customerId, materialNumber, serialNumber } = payload;
    const subscription = subscribeToDevicePausedState({
      customerId,
      materialNumber,
      serialNumber,
    }).subscribe({
      next: result => {
        const paused = resolveIsPaused(model, result.data?.device?.state);
        const { beep, blink } = resolveLocateSettings(result.data?.device?.settings);
        const deviceState = result.data?.device?.state?.deviceState ?? undefined;

        // An event may carry only part of the status; only report what it actually has.
        if (paused != null || beep != null || blink != null || deviceState != null) {
          emit({ kind: 'status', update: { paused, beep, blink, deviceState } });
        }
      },
      error: error => emit({ kind: 'error', error }),
    });

    return () => subscription.unsubscribe();
  });

// Loads and follows the paused state; throws if the initial query fails.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* followDevicePausedState(
  robotCleaningControlService: IDependencies['robotCleaningControlService'],
  payload: SubscribeToPausedStateAction['payload']
): Generator<unknown, SubscriptionFollowOutcome, any> {
  const { model, materialNumber, serialNumber } = payload;

  const robotKey = getRobotKey(materialNumber, serialNumber);

  // Raced so an unsubscribe during the query isn't missed and applied to the next robot.
  const { initial, stopped: stoppedEarly } = yield* race({
    initial: call(robotCleaningControlService.getDevicePausedState, {
      materialNumber,
      serialNumber,
    }),
    stopped: take(RobotCleaningControlActions.unsubscribeFromPausedState),
  });

  if (stoppedEarly) {
    return { kind: 'stopped' };
  }

  // Kept so the live map and the device commands don't have to query the device for its id again.
  if (initial?.device?.id) {
    yield* put(RobotCleaningControlActions.praDeviceIdReceived(initial.device.id));
  }

  // Resolved even when the device reports no state, so the controls don't stay locked waiting.
  yield* put(RobotCleaningControlActions.pausedStateReceived(resolveIsPaused(model, initial?.device?.state) ?? false));
  // Same as paused: resolved even when the device reports no settings, so Beep / Blink don't stay locked.
  const initialLocateSettings = resolveLocateSettings(initial?.device?.settings);

  // Defaults first, so a retry's missing value can't override a just-confirmed pin either.
  const { beep: initialBeep, blink: initialBlink } = dropSettledLocateContradictions(robotKey, {
    beep: initialLocateSettings.beep ?? false,
    blink: initialLocateSettings.blink ?? false,
  });

  if (initialBeep != null || initialBlink != null) {
    yield* put(RobotCleaningControlActions.locateSettingsReceived({ beep: initialBeep, blink: initialBlink }));
  }

  // Every device event carries its state, so only a change is reported.
  let lastDeviceState = initial?.device?.state?.deviceState ?? undefined;

  const channel = yield* call(
    createPausedStateChannel,
    robotCleaningControlService.subscribeToDevicePausedState,
    payload
  );

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { event } = yield* race({
        stopped: take(RobotCleaningControlActions.unsubscribeFromPausedState),
        event: take(channel),
      });

      if (!event) {
        return { kind: 'stopped' };
      }

      if (event.kind === 'error') {
        console.error(event.error);

        return { kind: 'failed', error: event.error };
      }

      const { paused, deviceState } = event.update;
      const { beep, blink } = dropSettledLocateContradictions(robotKey, event.update);

      if (paused != null) {
        yield* put(RobotCleaningControlActions.pausedStateReceived(paused));
      }

      if (deviceState != null && deviceState !== lastDeviceState) {
        lastDeviceState = deviceState;
        yield* put(RobotCleaningControlActions.deviceStateReported(deviceState));
      }

      if (beep != null || blink != null) {
        yield* put(RobotCleaningControlActions.locateSettingsReceived({ beep, blink }));
      }
    }
  } finally {
    channel.close();
  }
}

// Paused (B50 `paused`, CV50 `deviceState`) and CV50 Beep / Blink come from the device, not command results.
// Retries on failure, keeping the last known state.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* pausedStateSaga(action: SubscribeToPausedStateAction): Generator<unknown, void, any> {
  const { robotCleaningControlService } = yield* getContext<IDependencies>('dependencies');
  yield* followWithRetry({
    follow: () => followDevicePausedState(robotCleaningControlService, action.payload),
    stop: RobotCleaningControlActions.unsubscribeFromPausedState,
    // No state known yet: treat as not paused so the controls don't stay locked.
    onErrorBeforeFirstLoad: function* resolveAsNotPaused() {
      yield* put(RobotCleaningControlActions.pausedStateReceived(false));
      yield* put(RobotCleaningControlActions.locateSettingsReceived({ beep: false, blink: false }));
    },
  });
}

export function* robotCleaningControlSaga(): Generator<ForkEffect<never>, void> {
  // takeLeading ignores further requests while one is in flight, preventing duplicate/conflicting commands
  yield* takeLeading(RobotCleaningControlActions.sendCleaningCommandRequest, sendCleaningCommandSaga);
  yield* takeLeading(RobotCleaningControlActions.sendLocateCommandRequest, sendLocateCommandSaga);
  yield* takeLatest(RobotCleaningControlActions.subscribeToPausedState, pausedStateSaga);
}
